import React, { useState } from 'react';
import { Mail } from 'lucide-react';
import { Input, Textarea, Button } from './ui';

interface ContactSectionProps {
  contactRef: React.RefObject<HTMLDivElement>;
}

const ContactSection: React.FC<ContactSectionProps> = ({ contactRef }) => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({ email: '' });
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.id]: e.target.value });
    if (e.target.id === 'email') {
      setErrors({ ...errors, email: validateEmail(e.target.value) ? '' : 'Invalid email address' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.name && validateEmail(form.email) && form.message) {
      try {
        const response = await fetch('http://localhost:4000/send-email', {          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
        if (response.ok) {
          setNotification({ type: 'success', message: 'Email sent successfully.' });
          setForm({ name: '', email: '', message: '' });
        } else {
          setNotification({ type: 'error', message: 'Failed to send email.' });
        }
      } catch {
        setNotification({ type: 'error', message: 'Network error. Please try again later.' });
      }
    } else {
      setNotification({ type: 'error', message: 'Please fill out all fields correctly.' });
    }
  };

  return (
    <section ref={contactRef} id="contact" className="w-full py-12 md:py-24 lg:py-32">
      <div className="container px-4 md:px-6">
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight">
              Get in Touch
            </h2>
            <p className="text-muted-foreground md:text-xl">
              Have questions about our algorithmic trading solutions? Reach out to our team of experts.
            </p>
          </div>
          <form className="space-y-4" onSubmit={handleSubmit}>
            {notification && (
              <div className={`notification ${notification.type} p-4 mb-4 rounded ${
                notification.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {notification.message}
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Input id="name" placeholder="Enter your name" required value={form.name} onChange={handleChange} />
              </div>
              <div className="space-y-2">
                <Input id="email" type="email" placeholder="Enter your email" required value={form.email} onChange={handleChange} />
                {errors.email && <span className="text-red-500">{errors.email}</span>}
              </div>
            </div>
            <div className="space-y-2">
              <Textarea className="min-h-[100px] w-full border p-2 rounded" id="message" placeholder="Enter your message" required value={form.message} onChange={handleChange} />
            </div>
            <Button type="submit" variant="default">
              Send Message
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;